import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchRouterInterfaces,
  fetchRouterMonitoringRouters,
  fetchRouterTrafficCurrent,
} from '../lib/routerMonitoringApi';
import { routerMonitoringQueryKeys } from '../lib/routerMonitoringQueryKeys';
import { useRouterTrafficHistory } from './useRouterTrafficHistory';
import { resolveRouterOnlineState } from '../../../utils/routerStatus';

const toBool = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on', 'online', 'up', 'enabled', 'active'].includes(text)) return true;
  if (['0', 'false', 'no', 'off', 'offline', 'down', 'disabled', 'inactive'].includes(text)) return false;
  return fallback;
};

const scoreRouterForSelection = (row) => {
  let score = 0;
  if (row?.monitoringEnabled) score += 4;
  if (row?.trafficEnabled) score += 4;
  if (row?.isOnline) score += 8;
  if (String(row?.primaryUplinkInterface || '').trim()) score += 3;
  if (Array.isArray(row?.monitoredInterfaces) && row.monitoredInterfaces.length > 0) score += 2;
  if (Number(row?.activePppSessions || 0) > 0) score += 1;
  return score;
};

const normalizeRouters = (payload) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => ({
    ...resolveRouterOnlineState(item),
    routerId: String(item?.router_id || item?.id || '').trim(),
    name: String(item?.name || item?.identity || item?.nas_identifier || '-').trim(),
    vendor: String(item?.vendor || '-').trim(),
    mgmtIp: String(item?.mgmt_ip || item?.host || '').trim(),
    status: String(item?.status || '').trim().toLowerCase() || 'unknown',
    monitoringEnabled: toBool(item?.monitoring_enabled, true),
    trafficEnabled: toBool(item?.traffic_enabled, true),
    pppSessionSourceEnabled: toBool(item?.ppp_session_source_enabled, false),
    identity: String(item?.identity || '').trim(),
    version: String(item?.version || '').trim(),
    model: String(item?.model || '').trim(),
    nasIdentifier: String(item?.nas_identifier || '').trim(),
    managementMethod: String(item?.management_method || '').trim(),
    primaryUplinkInterface: String(item?.primary_uplink_interface || '').trim(),
    recommendedPrimaryUplinkInterface: String(item?.recommended_primary_uplink_interface || '').trim(),
    monitoredInterfaces: Array.isArray(item?.traffic_interface_whitelist) ? item.traffic_interface_whitelist.filter(Boolean) : [],
    recommendedTrafficInterfaceWhitelist: Array.isArray(item?.recommended_traffic_interface_whitelist) ? item.recommended_traffic_interface_whitelist.filter(Boolean) : [],
    activePppSessions: Number(item?.active_ppp_sessions || 0) || 0,
    lastDiscoveryAt: item?.last_discovery_at || null,
    lastDiscoveryStatus: String(item?.last_discovery_status || '').trim(),
    raw: item,
  }));
};

export const useRouterMonitoringData = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [selectedRouterId, setSelectedRouterId] = useState('');
  const [range, setRange] = useState('1h');
  const [interfaceName, setInterfaceName] = useState('');
  const [selectedInterfaces, setSelectedInterfaces] = useState([]);

  const routersQuery = useQuery({
    queryKey: routerMonitoringQueryKeys.routers({ search, status: statusFilter, vendor: vendorFilter }),
    queryFn: () => fetchRouterMonitoringRouters({
      search,
      status: statusFilter === 'all' ? undefined : statusFilter,
      vendor: vendorFilter === 'all' ? undefined : vendorFilter,
    }),
    refetchInterval: 30000,
  });

  const routers = useMemo(() => {
    const normalized = normalizeRouters(routersQuery.data);
    return normalized
      .filter((router) => router.monitoringEnabled !== false)
      .filter((router) => {
        if (statusFilter === 'online') return router.isOnline;
        if (statusFilter === 'offline') return !router.isOnline;
        return true;
      })
      .sort((a, b) => scoreRouterForSelection(b) - scoreRouterForSelection(a) || String(a.name || '').localeCompare(String(b.name || '')));
  }, [routersQuery.data, statusFilter]);

  useEffect(() => {
    if (!routers.length) {
      setSelectedRouterId('');
      return;
    }
    if (!selectedRouterId || !routers.some((r) => String(r.routerId || '') === String(selectedRouterId))) {
      const first = routers[0];
      setSelectedRouterId(String(first.routerId || ''));
    }
  }, [routers, selectedRouterId]);

  const currentQuery = useQuery({
    queryKey: routerMonitoringQueryKeys.trafficCurrent(selectedRouterId),
    queryFn: () => fetchRouterTrafficCurrent(selectedRouterId, { refresh: true }),
    enabled: Boolean(selectedRouterId),
    refetchInterval: 20000,
  });

  const selectedRouter = useMemo(
    () => routers.find((r) => String(r.routerId || '') === String(selectedRouterId)) || null,
    [routers, selectedRouterId],
  );

  const interfacesQuery = useQuery({
    queryKey: routerMonitoringQueryKeys.interfaces(selectedRouterId, false),
    queryFn: () => fetchRouterInterfaces(selectedRouterId, { refresh: false }),
    enabled: Boolean(selectedRouterId),
    refetchInterval: 20000,
  });

  useEffect(() => {
    const options = new Set([
      selectedRouter?.primaryUplinkInterface,
      selectedRouter?.recommendedPrimaryUplinkInterface,
      ...(selectedRouter?.monitoredInterfaces || []),
      ...((interfacesQuery.data?.interfaces || []).map((item) => item?.name).filter(Boolean)),
    ].filter(Boolean));
    const available = Array.from(options);
    if (available.length === 0) {
      setSelectedInterfaces([]);
      return;
    }
    setSelectedInterfaces((prev) => {
      const normalizedPrev = Array.isArray(prev) ? prev.filter((name) => available.includes(name)) : [];
      if (normalizedPrev.length > 0) return normalizedPrev;
      const preferred = [
        selectedRouter?.primaryUplinkInterface,
        selectedRouter?.recommendedPrimaryUplinkInterface,
      ].filter(Boolean);
      const next = preferred.filter((name, idx, arr) => available.includes(name) && arr.indexOf(name) === idx);
      return next.length > 0 ? next : [available[0]];
    });
  }, [selectedRouter, interfacesQuery.data]);

  const historyQuery = useRouterTrafficHistory({
    routerId: selectedRouterId,
    interfaceName: undefined,
    range,
  });

  return {
    filters: {
      search,
      setSearch,
      statusFilter,
      setStatusFilter,
      vendorFilter,
      setVendorFilter,
      range,
      setRange,
      interfaceName,
      setInterfaceName,
      selectedInterfaces,
      setSelectedInterfaces,
    },
    routersQuery,
    routers,
    selectedRouterId,
    setSelectedRouterId,
    selectedRouter,
    currentQuery,
    interfacesQuery,
    historyQuery,
  };
};

export default useRouterMonitoringData;
